import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { incidentApi } from '../../services/incidentApi';
import { useAuth } from '../../context/AuthContext';

const PRIORITY_MAP = {
  critica: { label: 'Critica', color: 'var(--red)', bg: 'var(--red-dim)' },
  alta:    { label: 'Alta',    color: '#D45D00', bg: 'var(--amber-dim)' },
  media:   { label: 'Media',   color: 'var(--ink-2)', bg: 'var(--bg-subtle)' },
  baja:    { label: 'Baja',    color: 'var(--green)', bg: 'var(--green-dim)' },
};
const STATUS_OPTIONS = ['nuevo','en_progreso','pendiente','resuelto','cerrado'];
const STATUS_MAP = {
  nuevo:       { color: 'var(--accent)', bg: '#E6F1FB', label: 'Nuevo' },
  en_progreso: { color: '#D45D00', bg: 'var(--amber-dim)', label: 'En progreso' },
  pendiente:   { color: 'var(--ink-2)', bg: 'var(--bg-subtle)', label: 'Pendiente' },
  resuelto:    { color: 'var(--green)', bg: 'var(--green-dim)', label: 'Resuelto' },
  cerrado:     { color: 'var(--ink-2)', bg: 'var(--bg-subtle)', label: 'Cerrado' },
};

function Avatar({ name, size = 32, color = 'var(--accent)' }) {
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--surface)', fontSize: size * 0.38, fontWeight: 600, flexShrink: 0 }}>
      {name?.[0]?.toUpperCase() || '?'}
    </div>
  );
}

function SLAIndicator({ inc }) {
  const isDone = ['resuelto', 'cerrado'].includes(inc.status);
  const calcPct = (target) => {
    if (!target) return 0;
    const total = new Date(target) - new Date(inc.created_at);
    const elapsed = (isDone ? new Date(inc.resolved_at || inc.closed_at) : new Date()) - new Date(inc.created_at);
    return Math.min(100, Math.max(0, Math.round(elapsed / total * 100)));
  };
  const responsePct = calcPct(inc.sla_response_at);
  const resolvePct = calcPct(inc.sla_resolve_at);
  const getColor = (pct, done) => done ? 'var(--green)' : pct >= 100 ? 'var(--red)' : pct >= 80 ? '#D45D00' : 'var(--green)';
  const responseColor = getColor(responsePct, !!inc.responded_at);
  const resolveColor = getColor(resolvePct, isDone);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {[
        { label: 'Primera respuesta', pct: responsePct, color: responseColor, done: !!inc.responded_at, target: inc.sla_response_at },
        { label: 'Resolución', pct: resolvePct, color: resolveColor, done: isDone, target: inc.sla_resolve_at },
      ].map(({ label, pct, color, done, target }) => (
        <div key={label}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 11, color: '#666' }}>{label}</span>
            <span style={{ fontSize: 11, fontWeight: 500, color }}>{done ? '✓ OK' : pct >= 100 ? 'Vencido' : `${pct}%`}</span>
          </div>
          <div style={{ height: 5, background: '#eee', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4 }} />
          </div>
          <div style={{ fontSize: 10, color: '#aaa', marginTop: 2 }}>
            Limite: {target ? new Date(target).toLocaleString('es-AR') : '—'}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function IncidentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [inc, setInc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [addingComment, setAddingComment] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [checklist, setChecklist] = useState(null);
  const [checklistTypes, setChecklistTypes] = useState([]);
  const [creatingChecklist, setCreatingChecklist] = useState(false);
  const [selectedChecklistType, setSelectedChecklistType] = useState('ransomware');
  const [attachments, setAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [activeTab, setActiveTab] = useState('conversation'); // conversation | ai-resolver
  const [aiPlan, setAiPlan] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiExecuting, setAiExecuting] = useState(false);
  const [aiResults, setAiResults] = useState(null);
  const [selectedActions, setSelectedActions] = useState([]);
  const [confirmedActions, setConfirmedActions] = useState({}); // id -> true (acciones sensibles confirmadas por el operador)
  const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');
  const commentsEndRef = useRef(null);
  const esRef = useRef(null);
  const replyFileRef = useRef(null);
  const editorRef = useRef(null);

  const token = localStorage.getItem('token');
  const authH = { Authorization: 'Bearer ' + token };

  const loadAttachments = () => {
    fetch(`${API_URL}/incidents/${id}/attachments`, { headers: authH })
      .then(r => r.json()).then(data => setAttachments(Array.isArray(data) ? data : [])).catch(() => {});
  };

  const loadChecklist = () => {
    fetch(API_URL + '/checklists/incident/' + id, { headers: authH })
      .then(r => r.json()).then(data => setChecklist(data)).catch(() => {});
    fetch(API_URL + '/checklists/types', { headers: authH })
      .then(r => r.json()).then(data => setChecklistTypes(Array.isArray(data) ? data : [])).catch(() => {});
  };
  const load = () => incidentApi.getOne(id).then(setInc).catch(() => navigate('/incidents')).finally(() => { setLoading(false); loadChecklist(); loadAttachments(); });
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    const es = incidentApi.subscribeEvents((event) => {
      if ((event.type === 'update_incident' && event.incident?.id === id) ||
          (event.type === 'new_comment' && event.incident_id === id)) {
        load();
      }
    });
    esRef.current = es;
    return () => es.close();
  }, [id]);

  useEffect(() => {
    if (inc?.comments?.length) commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [inc?.comments?.length]);

  const handleStatusChange = async (status) => {
    setUpdatingStatus(true);
    try { await incidentApi.update(id, { status }); load(); }
    catch (e) { alert(e.message); }
    finally { setUpdatingStatus(false); }
  };

  const handleAssign = async () => {
    try { await incidentApi.update(id, { assigned_to: user.id }); load(); }
    catch (e) { alert(e.message); }
  };

  const handleUnassign = async () => {
    try { await incidentApi.update(id, { assigned_to: null }); load(); }
    catch (e) { alert(e.message); }
  };

  const handleComment = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const body = editorRef.current?.innerHTML || comment;
    const plainText = editorRef.current?.innerText?.trim() || comment.trim();
    if (!plainText && pendingFiles.length === 0) return;
    setAddingComment(true);
    try {
      const newComment = await incidentApi.addComment(id, { content: body, is_internal: isInternal });
      // Subir archivos vinculados al comentario
      if (pendingFiles.length > 0 && newComment?.id) {
        const fd = new FormData();
        pendingFiles.forEach(f => fd.append('files', f));
        fd.append('comment_id', newComment.id);
        const uploadRes = await fetch(`${API_URL}/incidents/${id}/attachments`, { method: 'POST', headers: authH, body: fd });
        if (!uploadRes.ok) {
          const errData = await uploadRes.json().catch(() => ({}));
          console.warn('Error subiendo adjuntos:', errData.error || uploadRes.status);
        }
      }
      setComment('');
      setPendingFiles([]);
      if (editorRef.current) editorRef.current.innerHTML = '';
      load();
    } catch (e) { alert(e.message); }
    finally { setAddingComment(false); }
  };

  const handleReplyFileAdd = (e) => {
    const files = Array.from(e.target.files || []);
    setPendingFiles(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const removePendingFile = (idx) => setPendingFiles(prev => prev.filter((_, i) => i !== idx));

  const handleDeleteAttachment = async (attId) => {
    if (!window.confirm('¿Eliminar este adjunto?')) return;
    await fetch(`${API_URL}/incidents/attachments/${attId}`, { method: 'DELETE', headers: authH });
    loadAttachments();
  };

  const fileIcon = (mime) => {
    if (!mime) return '📎';
    if (mime.startsWith('image/')) return '🖼️';
    if (mime.includes('pdf')) return '📄';
    if (mime.includes('word')) return '📝';
    if (mime.includes('excel') || mime.includes('sheet')) return '📊';
    return '📎';
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('es-AR') : '—';

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!inc) return null;


  const handleCreateChecklist = async () => {
    setCreatingChecklist(true);
    try {
      await fetch(API_URL + '/checklists/incident/' + id, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authH },
        body: JSON.stringify({ checklist_type: selectedChecklistType })
      });
      loadChecklist();
    } catch(e) { alert(e.message); }
    finally { setCreatingChecklist(false); }
  };

  const handleToggleItem = async (itemId, isCompleted) => {
    await fetch(API_URL + '/checklists/items/' + itemId, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authH },
      body: JSON.stringify({ is_completed: isCompleted })
    });
    loadChecklist();
  };

  const analyzeWithAI = async () => {
    setAiLoading(true);
    setAiError(null);
    setAiPlan(null);
    setAiResults(null);
    setSelectedActions([]);
    try {
      const r = await fetch(`${API_URL}/ai-actions/analyze`, {
        method: 'POST',
        headers: { ...authH, 'Content-Type': 'application/json' },
        body: JSON.stringify({ incident_id: id }),
      });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error || 'Error desconocido');
      setAiPlan(d.plan);
      setSelectedActions(d.plan.actions.map(a => a.id));
    } catch (e) {
      setAiError(e.message);
    } finally {
      setAiLoading(false);
    }
  };

  const executeAIActions = async () => {
    if (!aiPlan || selectedActions.length === 0) return;
    setAiExecuting(true);
    try {
      // Propagar la confirmación explícita del operador por acción sensible.
      const actions = aiPlan.actions
        .filter(a => selectedActions.includes(a.id))
        .map(a => ({ ...a, confirmed: !!confirmedActions[a.id] }));
      const r = await fetch(`${API_URL}/ai-actions/execute`, {
        method: 'POST',
        headers: { ...authH, 'Content-Type': 'application/json' },
        body: JSON.stringify({ incident_id: id, actions }),
      });
      const d = await r.json();
      setAiResults(d.results || []);
      load(); // reload incident
    } catch (e) {
      setAiError(e.message);
    } finally {
      setAiExecuting(false);
    }
  };

  const pri = PRIORITY_MAP[inc.priority] || PRIORITY_MAP.media;
  const st = STATUS_MAP[inc.status] || STATUS_MAP.nuevo;
  const isTech = user?.role === 'admin' || user?.role === 'technician';

  // Acciones sensibles que exigen confirmación explícita antes de ejecutar
  // (espejo del guardrail server-side en ai-actions.js).
  const AI_CONFIRM_TYPES = [
    'reset_m365_password', 'disable_m365_user', 'remove_m365_license',
    'set_mailbox_forwarding', 'grant_mailbox_access', 'convert_to_shared_mailbox',
    'add_m365_alias', 'add_m365_email_address',
  ];
  const aiActionNeedsConfirm = (a) => a.risk === 'high' || AI_CONFIRM_TYPES.includes(a.type);
  const pendingConfirm = (aiPlan?.actions || []).some(
    a => selectedActions.includes(a.id) && aiActionNeedsConfirm(a) && !confirmedActions[a.id]
  );

  return (
    <div style={{ maxWidth: 1100 }}>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 16, color: 'var(--gray-500)' }}>
        <Link to="/incidents" style={{ color: 'var(--blue)', textDecoration: 'none' }}>Incidentes</Link>
        <span>›</span>
        <span className="mono">#{inc.ticket_number}</span>
      </div>

      {/* Header */}
      <div className="card" style={{ padding: '16px 20px', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <span className="mono" style={{ fontSize: 12, color: 'var(--gray-500)', background: 'var(--gray-50)', padding: '2px 8px', borderRadius: 6 }}>#{inc.ticket_number}</span>
              <span style={{ color: pri.color, fontSize: 11, fontWeight: 500 }}>{pri.label}</span>
              <span style={{ color: st.color, fontSize: 11, fontWeight: 500 }}>{st.label}</span>
              <span style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'capitalize', background: 'var(--gray-50)', padding: '3px 8px', borderRadius: 6 }}>{inc.category}</span>
              <span style={{ fontSize: 11, color: 'var(--gray-500)', background: 'var(--gray-50)', padding: '3px 8px', borderRadius: 6 }}>Origen: Web</span>
            </div>
            <h1 style={{ fontSize: 19, fontWeight: 600, color: 'var(--gray-900)', marginBottom: 4 }}>{inc.title}</h1>
            <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>
              Reportado por <strong>{inc.reported_by_name}</strong> · {formatDate(inc.created_at)}
            </div>
          </div>

          {isTech && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <select className="form-input" style={{ width: 170 }} value={inc.status} onChange={e => handleStatusChange(e.target.value)} disabled={updatingStatus}>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_MAP[s]?.label || s}</option>)}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      {isTech && (
        <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid var(--gray-200)' }}>
          {[
            { key: 'conversation', label: 'Conversación' },
            { key: 'ai-resolver',  label: '✦ Resolver con IA' },
          ].map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              style={{
                padding: '8px 20px', border: 'none', background: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: activeTab === t.key ? 600 : 400,
                color: activeTab === t.key ? 'var(--blue)' : 'var(--gray-500)',
                borderBottom: activeTab === t.key ? '2px solid var(--blue)' : '2px solid transparent',
                marginBottom: -1,
              }}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* AI Resolver Panel */}
      {isTech && activeTab === 'ai-resolver' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16, alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {!aiPlan && !aiLoading && (
              <div className="card" style={{ padding: 32, textAlign: 'center' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>✦</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--gray-900)', marginBottom: 8 }}>
                  Análisis con Inteligencia Artificial
                </div>
                <div style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 20, maxWidth: 400, margin: '0 auto 20px' }}>
                  Claude analizará el ticket y propondrá acciones concretas para resolverlo, incluyendo acciones en Microsoft 365 si están configuradas.
                </div>
                {aiError && (
                  <div style={{ background: 'var(--red-dim)', color: '#B0291D', border: '1px solid #F5C0BC', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13 }}>
                    {aiError}
                  </div>
                )}
                <button className="btn btn-primary" onClick={analyzeWithAI} style={{ fontSize: 14, padding: '10px 28px' }}>
                  Analizar con IA
                </button>
              </div>
            )}

            {aiLoading && (
              <div className="card" style={{ padding: 40, textAlign: 'center' }}>
                <div className="spinner" style={{ margin: '0 auto 16px' }} />
                <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Claude está analizando el ticket...</div>
              </div>
            )}

            {aiPlan && !aiLoading && (
              <div>
                {/* Diagnóstico */}
                <div className="card" style={{ padding: 20, marginBottom: 12, borderLeft: '3px solid var(--blue)' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--blue)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                    Diagnóstico IA
                  </div>
                  <div style={{ fontSize: 14, color: 'var(--gray-900)', lineHeight: 1.6 }}>
                    {aiPlan.diagnosis}
                  </div>
                  <div style={{ marginTop: 8, fontSize: 11, color: 'var(--gray-400)' }}>
                    Confianza: <span style={{ fontWeight: 600, color: aiPlan.confidence === 'high' ? 'var(--green)' : aiPlan.confidence === 'medium' ? '#D45D00' : '#B0291D' }}>
                      {aiPlan.confidence === 'high' ? 'Alta' : aiPlan.confidence === 'medium' ? 'Media' : 'Baja'}
                    </span>
                    {!aiPlan.m365_available && <span style={{ marginLeft: 12, color: '#D45D00' }}>⚠ M365 no configurado</span>}
                  </div>
                </div>

                {/* Acciones propuestas */}
                <div className="card" style={{ padding: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 16 }}>
                    Plan de acción ({aiPlan.actions?.length || 0} acciones)
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                    {aiPlan.actions?.map(a => {
                      const riskColor = { none: 'var(--green)', low: 'var(--green)', medium: '#D45D00', high: '#B0291D' }[a.risk] || 'var(--ink-2)';
                      const isSelected = selectedActions.includes(a.id);
                      const isM365 = a.type.includes('m365');
                      return (
                        <div key={a.id}
                          onClick={() => setSelectedActions(prev => isSelected ? prev.filter(x => x !== a.id) : [...prev, a.id])}
                          style={{
                            border: `1px solid ${isSelected ? 'var(--blue)' : 'var(--gray-200)'}`,
                            borderRadius: 8, padding: '12px 14px', cursor: 'pointer',
                            background: isSelected ? '#E6F1FB' : 'var(--gray-50)',
                            display: 'flex', gap: 12, alignItems: 'flex-start',
                          }}>
                          <div style={{ width: 18, height: 18, borderRadius: 4, border: isSelected ? 'none' : '1.5px solid var(--gray-300)', background: isSelected ? 'var(--blue)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                            {isSelected && <span style={{ color: 'var(--surface)', fontSize: 11 }}>✓</span>}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-900)' }}>{a.description}</span>
                              {isM365 && <span style={{ fontSize: 10, background: '#E6F1FB', color: 'var(--blue)', padding: '1px 6px', borderRadius: 4 }}>M365</span>}
                              {a.risk && a.risk !== 'none' && <span style={{ fontSize: 10, color: riskColor, background: '#f5f5f5', padding: '1px 6px', borderRadius: 4 }}>Riesgo: {a.risk}</span>}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--gray-500)', fontFamily: 'monospace' }}>
                              {a.type}
                              {a.params?.user_principal_name && ` → ${a.params.user_principal_name}`}
                              {a.params?.forward_to_email && ` → ${a.params.forward_to_email}`}
                              {a.params?.grantee_upn && ` → ${a.params.grantee_upn}`}
                              {a.params?.text && ` → "${a.params.text.substring(0, 60)}${a.params.text.length > 60 ? '...' : ''}"`}
                            </div>
                            {isSelected && aiActionNeedsConfirm(a) && (
                              <label onClick={e => e.stopPropagation()}
                                style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 11, fontWeight: 600, color: '#B0291D', cursor: 'pointer' }}>
                                <input type="checkbox" checked={!!confirmedActions[a.id]}
                                  onChange={e => { e.stopPropagation(); setConfirmedActions(prev => ({ ...prev, [a.id]: e.target.checked })); }} />
                                ⚠ Acción sensible — confirmo ejecutarla
                              </label>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {aiResults && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-700)', marginBottom: 8 }}>Resultado de ejecución:</div>
                      {aiResults.map(r => {
                        const amber = r.needs_confirmation;
                        return (
                          <div key={r.id} style={{ fontSize: 12, padding: '6px 10px', background: r.ok ? 'var(--green-dim)' : amber ? '#FFF6E6' : 'var(--red-dim)', borderRadius: 6, marginBottom: 4, color: r.ok ? 'var(--green)' : amber ? 'var(--amber)' : '#B0291D' }}>
                            {r.ok ? '✓' : amber ? '⚠' : '✗'} {r.type}: {r.ok ? (r.result?.message || 'OK') : r.error}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {aiError && (
                    <div style={{ background: 'var(--red-dim)', color: '#B0291D', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13 }}>
                      {aiError}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-secondary" onClick={analyzeWithAI} style={{ fontSize: 12 }}>
                      Volver a analizar
                    </button>
                    <button className="btn btn-primary" onClick={executeAIActions}
                      disabled={aiExecuting || selectedActions.length === 0 || pendingConfirm}
                      title={pendingConfirm ? 'Confirmá las acciones sensibles marcadas antes de ejecutar' : ''}
                      style={{ fontSize: 12, flex: 1 }}>
                      {aiExecuting ? 'Ejecutando...' : `Ejecutar ${selectedActions.length} acción${selectedActions.length !== 1 ? 'es' : ''}`}
                    </button>
                  </div>
                  {pendingConfirm && (
                    <div style={{ marginTop: 8, fontSize: 11, color: '#B0291D' }}>
                      ⚠ Hay acciones sensibles seleccionadas. Confirmá cada una (checkbox) antes de ejecutar.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar igual que conversación */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
            <div className="card" style={{ padding: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Sobre el ticket</div>
              {[
                ['Prioridad', <span style={{ background: pri.bg, color: pri.color, padding: '2px 8px', borderRadius: 4, fontSize: 12 }}>{pri.label}</span>],
                ['Estado', <span style={{ background: st.bg, color: st.color, padding: '2px 8px', borderRadius: 4, fontSize: 12 }}>{st.label}</span>],
                ['Categoría', inc.category],
                ['Equipo', inc.asset_hostname || '—'],
                ['Reportado por', inc.reported_by_name],
              ].map(([l, v]) => (
                <div key={l} style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 2 }}>{l}</div>
                  <div>{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: activeTab === 'conversation' || !isTech ? 'grid' : 'none', gridTemplateColumns: '1fr 280px', gap: 16, alignItems: 'start' }}>
        {/* LEFT: conversation */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* Descripcion */}
          <div className="card" style={{ padding: 20 }}>
            <div style={{ display: 'flex', gap: 12 }}>
              <Avatar name={inc.reported_by_name} color="var(--accent)" />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{inc.reported_by_name}</span>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, background: '#E6F1FB', color: 'var(--accent)', padding: '2px 8px', borderRadius: 10 }}>CLIENTE</span>
                    <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{formatDate(inc.created_at)}</span>
                  </div>
                </div>
                <div className="comment-body" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(inc.description || 'Sin descripción.') }} />
              </div>
            </div>
          </div>

          {/* Comments */}
          {inc.comments?.map(c => {
            const commentAtts = attachments.filter(a => a.comment_id === c.id);
            return (
              <div key={c.id} className="card" style={{ padding: 16, borderLeft: c.is_internal ? '3px solid #D45D00' : '3px solid transparent' }}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <Avatar name={c.author_name} color={c.author_role === 'user' ? 'var(--ink-2)' : 'var(--accent)'} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontWeight: 600, fontSize: 14 }}>{c.author_name}</span>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        {c.is_internal && <span style={{ fontSize: 10, color: '#D45D00', fontWeight: 500 }}>NOTA INTERNA</span>}
                        <span style={{ fontSize: 11, background: c.author_role === 'user' ? 'var(--gray-100)' : '#E6F1FB', color: c.author_role === 'user' ? 'var(--gray-500)' : 'var(--accent)', padding: '2px 8px', borderRadius: 10 }}>{c.author_role === 'user' ? 'CLIENTE' : 'AGENTE'}</span>
                        <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{formatDate(c.created_at)}</span>
                      </div>
                    </div>
                    <div className="comment-body" style={{ marginBottom: commentAtts.length ? 10 : 0 }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.body || '') }} />
                    {/* Adjuntos del comentario */}
                    {commentAtts.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                        {commentAtts.map(att => {
                          const isImg = att.mimetype?.startsWith('image/');
                          const attUrl = `${API_URL}/incidents/attachments/${att.id}`;
                          return (
                            <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 8 }}>
                              <span style={{ fontSize: 15 }}>{fileIcon(att.mimetype)}</span>
                              {isImg ? (
                                <button onClick={() => setLightbox(attUrl)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: 'var(--accent)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {att.original_name}
                                </button>
                              ) : (
                                <a href={attUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'var(--accent)', textDecoration: 'none', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                                  {att.original_name}
                                </a>
                              )}
                              <span style={{ fontSize: 10, color: 'var(--ink-3)', flexShrink: 0 }}>{att.size_bytes ? (att.size_bytes / 1024).toFixed(0) + ' KB' : ''}</span>
                              {(user?.role === 'admin') && (
                                <button onClick={() => handleDeleteAttachment(att.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', fontSize: 13, padding: '0 2px', lineHeight: 1 }}>✕</button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Adjuntos del incidente sin comentario (subidos en la creación) */}
          {attachments.filter(a => !a.comment_id).length > 0 && (
            <div className="card" style={{ padding: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 10 }}>Archivos adjuntos del ticket</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {attachments.filter(a => !a.comment_id).map(att => {
                  const isImg = att.mimetype?.startsWith('image/');
                  const attUrl = `${API_URL}/incidents/attachments/${att.id}`;
                  return (
                    <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 8 }}>
                      <span style={{ fontSize: 15 }}>{fileIcon(att.mimetype)}</span>
                      {isImg ? (
                        <button onClick={() => setLightbox(attUrl)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: 'var(--accent)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {att.original_name}
                        </button>
                      ) : (
                        <a href={attUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'var(--accent)', textDecoration: 'none', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                          {att.original_name}
                        </a>
                      )}
                      <span style={{ fontSize: 10, color: 'var(--ink-3)', flexShrink: 0 }}>{att.size_bytes ? (att.size_bytes / 1024).toFixed(0) + ' KB' : ''}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div ref={commentsEndRef} />

          {/* Agregar respuesta — estilo editor */}
          <div className="card" style={{ padding: 0, overflow: 'hidden', border: isInternal ? '1.5px solid #f59e0b' : '1px solid var(--border)' }}>

            {/* Toolbar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '8px 12px', borderBottom: '1px solid var(--bg)', background: 'var(--bg-subtle)', flexWrap: 'wrap' }}>
              {[
                { cmd: 'undo',            icon: '↩', title: 'Deshacer' },
                { cmd: 'redo',            icon: '↪', title: 'Rehacer' },
                { sep: true },
                { cmd: 'bold',            icon: 'B', title: 'Negrita',    style: { fontWeight: 700 } },
                { cmd: 'italic',          icon: 'I', title: 'Cursiva',    style: { fontStyle: 'italic' } },
                { cmd: 'underline',       icon: 'U', title: 'Subrayado',  style: { textDecoration: 'underline' } },
                { cmd: 'strikeThrough',   icon: 'S', title: 'Tachado',    style: { textDecoration: 'line-through' } },
                { sep: true },
                { cmd: 'insertOrderedList',   icon: '1≡', title: 'Lista numerada' },
                { cmd: 'insertUnorderedList', icon: '•≡', title: 'Lista' },
                { cmd: 'formatBlock',     icon: '❝', title: 'Cita', arg: 'blockquote' },
              ].map((b, i) => b.sep
                ? <div key={i} style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 4px' }} />
                : (
                  <button key={i} type="button" title={b.title}
                    onMouseDown={e => { e.preventDefault(); document.execCommand(b.cmd, false, b.arg || null); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '3px 7px', borderRadius: 4, fontSize: 13, color: 'var(--ink-2)', lineHeight: 1, ...b.style }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--border)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    {b.icon}
                  </button>
                )
              )}
            </div>

            {/* Área editable */}
            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              onInput={e => setComment(e.currentTarget.innerHTML)}
              data-placeholder="Escribí tu respuesta..."
              style={{
                minHeight: 180,
                padding: '14px 16px',
                fontSize: 14,
                lineHeight: 1.7,
                color: 'var(--ink)',
                outline: 'none',
                background: isInternal ? 'var(--amber-dim)' : '#fff',
                fontFamily: 'inherit',
                overflowY: 'auto',
              }}
            />

            {/* Archivos adjuntos pendientes */}
            {pendingFiles.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 14px', borderTop: '1px solid var(--bg)', background: 'var(--bg-subtle)' }}>
                {pendingFiles.map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px', background: 'var(--accent-dim)', border: '1px solid #bfdbfe', borderRadius: 20, fontSize: 12 }}>
                    <span>{fileIcon(f.type)}</span>
                    <span style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--accent)' }}>{f.name}</span>
                    <button type="button" onClick={() => removePendingFile(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-2)', fontSize: 13, lineHeight: 1, padding: 0 }}>✕</button>
                  </div>
                ))}
              </div>
            )}

            {/* Barra inferior */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderTop: '1px solid var(--bg)', background: 'var(--bg-subtle)' }}>
              {/* Íconos adjuntar */}
              <div style={{ display: 'flex', gap: 4 }}>
                {[
                  { accept: 'image/*',                              icon: '🖼️', title: 'Adjuntar imagen' },
                  { accept: '.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv', icon: '📄', title: 'Adjuntar documento' },
                  { accept: 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv', icon: '📎', title: 'Adjuntar archivo' },
                ].map((btn, i) => (
                  <React.Fragment key={i}>
                    <button type="button" title={btn.title}
                      onClick={() => { const inp = document.createElement('input'); inp.type='file'; inp.multiple=true; inp.accept=btn.accept; inp.onchange=e=>handleReplyFileAdd(e); inp.click(); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 6, fontSize: 18, lineHeight: 1, color: 'var(--ink-2)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--border)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                      {btn.icon}
                    </button>
                  </React.Fragment>
                ))}
                <input ref={replyFileRef} type="file" multiple style={{ display: 'none' }}
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                  onChange={handleReplyFileAdd} />
              </div>

              {/* Derecha: Interno + Responder */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {isTech && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: isInternal ? 'var(--amber)' : 'var(--gray-500)', cursor: 'pointer', fontWeight: isInternal ? 600 : 400 }}>
                    <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} />
                    Interno
                  </label>
                )}
                <button onClick={handleComment} className="btn btn-primary btn-sm"
                  disabled={addingComment || (!comment.trim() && pendingFiles.length === 0)}>
                  {addingComment ? 'Enviando...' : 'Responder'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* Participantes */}
          <div className="card" style={{ padding: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>Participantes</div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 8 }}>Cliente</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar name={inc.reported_by_name} size={28} color="var(--ink-2)" />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{inc.reported_by_name}</div>
                  <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{inc.reported_by_email}</div>
                </div>
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 8 }}>Agente asignado</div>
              {inc.assigned_to_name ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <Avatar name={inc.assigned_to_name} size={28} color="var(--accent)" />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{inc.assigned_to_name}</div>
                      <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>Agente</div>
                    </div>
                  </div>
                  {isTech && (
                    <button onClick={handleUnassign} style={{ fontSize: 11, color: 'var(--gray-500)', background: 'none', border: '1px solid var(--gray-200)', borderRadius: 6, padding: '3px 8px', cursor: 'pointer' }}>
                      Desasignar
                    </button>
                  )}
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 13, color: 'var(--gray-400)', fontStyle: 'italic', marginBottom: 8 }}>Sin asignar</div>
                  {isTech && (
                    <button onClick={handleAssign} className="btn btn-primary btn-sm" style={{ width: '100%' }}>
                      Asignarme
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* SLA */}
          <div className="card" style={{ padding: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>SLA</div>
            <SLAIndicator inc={inc} />
          </div>

          {/* Detalles */}
          <div className="card" style={{ padding: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>Detalles</div>
            {[
              ['Equipo afectado', inc.asset_hostname || '—'],
              ['Creado', formatDate(inc.created_at)],
              ['Actualizado', formatDate(inc.updated_at)],
              ['Respondido', formatDate(inc.responded_at)],
              ['Resuelto', formatDate(inc.resolved_at)],
            ].map(([label, value]) => (
              <div key={label} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: 13, color: 'var(--gray-900)' }}>{value}</div>
              </div>
            ))}
          </div>

          {/* Acciones rapidas */}
          {isTech && !['resuelto','cerrado'].includes(inc.status) && (
            <div className="card" style={{ padding: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>Acciones rapidas</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {inc.status === 'nuevo' && <button className="btn btn-primary btn-sm" onClick={() => handleStatusChange('en_progreso')}>Tomar ticket</button>}
                {inc.status === 'en_progreso' && <button className="btn btn-secondary btn-sm" onClick={() => handleStatusChange('pendiente')}>Marcar pendiente</button>}
                <button className="btn btn-secondary btn-sm" onClick={() => handleStatusChange('resuelto')} style={{ color: 'var(--green)', borderColor: 'var(--green)' }}>Marcar resuelto</button>
              </div>
            </div>
          )}
          {/* Checklist de seguridad */}
          {isTech && inc.category === 'seguridad' && (
            <div className="card" style={{ padding: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Checklist de respuesta</div>
              {checklist ? (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-700)', marginBottom: 8 }}>{checklist.checklist_type}</div>
                  <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 12 }}>
                    {checklist.completed_items}/{checklist.total_items} pasos completados
                  </div>
                  <div style={{ background: 'var(--gray-100)', borderRadius: 4, height: 6, marginBottom: 12 }}>
                    <div style={{ background: 'var(--green)', borderRadius: 4, height: 6, width: (checklist.completed_items / checklist.total_items * 100) + '%', transition: 'width 0.3s' }} />
                  </div>
                  {checklist.phases?.map(phase => (
                    <div key={phase.name} style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--blue)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{phase.name}</div>
                      {phase.items.map(item => (
                        <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6, padding: '6px 8px', borderRadius: 6, background: item.is_completed ? 'var(--green-dim)' : 'transparent', cursor: 'pointer' }}
                          onClick={() => handleToggleItem(item.id, !item.is_completed)}>
                          <div style={{ width: 16, height: 16, borderRadius: 3, border: item.is_completed ? 'none' : '1.5px solid #ccc', background: item.is_completed ? 'var(--green)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                            {item.is_completed && <span style={{ color: 'var(--surface)', fontSize: 10, lineHeight: 1 }}>✓</span>}
                          </div>
                          <div>
                            <div style={{ fontSize: 12, color: item.is_completed ? 'var(--green)' : 'var(--gray-700)', textDecoration: item.is_completed ? 'line-through' : 'none', fontWeight: item.is_required ? 500 : 400 }}>
                              {item.is_required && <span style={{ color: 'var(--red)', marginRight: 2 }}>*</span>}
                              {item.title}
                            </div>
                            {item.completed_by_name && (
                              <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>{item.completed_by_name}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 10 }}>Agregar checklist de respuesta a este incidente:</div>
                  <select className="form-input" value={selectedChecklistType} onChange={e => setSelectedChecklistType(e.target.value)} style={{ width: '100%', marginBottom: 8, padding: '6px 10px', fontSize: 12 }}>
                    {checklistTypes.map(t => <option key={t.key} value={t.key}>{t.name} ({t.total_steps} pasos)</option>)}
                  </select>
                  <button className="btn btn-primary" onClick={handleCreateChecklist} disabled={creatingChecklist} style={{ width: '100%', fontSize: 12 }}>
                    {creatingChecklist ? 'Creando...' : '+ Agregar checklist'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox imagen */}
      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, cursor: 'zoom-out' }}>
          <img src={lightbox} alt="adjunto" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8, boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }} onClick={e => e.stopPropagation()} />
          <button onClick={() => setLightbox(null)} style={{ position: 'absolute', top: 16, right: 20, background: 'none', border: 'none', color: 'var(--surface)', fontSize: 28, cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>
      )}
    </div>
  );
}
